import { authEvents } from '@/shared/lib/auth-events';
import { authLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { AuthState } from '../model/auth.store';

/**
 * Setup event listeners for auth store
 * This connects the event bus to the auth store
 */
export function setupAuthStoreEventListeners(
  setState: (state: Partial<AuthState>) => void
): () => void {
  const unsubscribers: (() => void)[] = [];

  // Listen for logout events from other sources
  unsubscribers.push(
    authEvents.on('auth:logout', () => {
      authLogger.debug('Handling logout event in store');
      // User entity lives in the Query cache — drop it together with the
      // token/auth flags so listeners see a single coherent reset.
      queryClient.removeQueries({ queryKey: cacheKeys.user });
      setState({
        token: null,
        isAuthenticated: false,
      });
    })
  );

  // Listen for error events
  unsubscribers.push(
    authEvents.on('auth:error', (event) => {
      authLogger.error('Auth error event received:', event.payload);
      // Could update error states based on context
      const payload = event.payload as
        | { error: Error; context?: string }
        | undefined;
      if (payload?.context === 'login') {
        setState({ loginError: payload.error?.message || 'An error occurred' });
      } else if (payload?.context === 'register') {
        setState({
          registerError: payload.error?.message || 'An error occurred',
        });
      }
    })
  );

  // Return cleanup function
  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}
